import { CreditCard, Landmark, ShieldCheck, Slash } from "lucide-react";
import { cn } from "@/lib/utils";

export type PricingTrustIcon = "card-off" | "mad" | "bank" | "shield";
export type PricingTrustTone = "purple" | "green" | "blue";

export type PricingTrustItem = {
  icon: PricingTrustIcon;
  iconLabel?: string;
  tone: PricingTrustTone;
  title: string;
  subtitle: string;
};

type PricingTrustRowProps = {
  items: readonly PricingTrustItem[];
};

const TONE_STYLES: Record<
  PricingTrustTone,
  {
    circle: string;
    icon: string;
  }
> = {
  purple: {
    circle: "border-violet-200 bg-violet-50 text-violet-700",
    icon: "text-violet-700",
  },
  green: {
    circle: "border-green-200 bg-green-50 text-[#08A62F]",
    icon: "text-[#08A62F]",
  },
  blue: {
    circle: "border-blue-200 bg-blue-50 text-blue-600",
    icon: "text-blue-600",
  },
};

function TrustIcon({ icon, iconLabel, tone }: { icon: PricingTrustIcon; iconLabel?: string; tone: PricingTrustTone }) {
  const styles = TONE_STYLES[tone];

  if (icon === "mad") {
    return (
      <span
        className={cn(
          "flex h-11 w-11 shrink-0 items-center justify-center rounded-full border text-sm font-black",
          styles.circle,
        )}
        aria-hidden
      >
        {iconLabel}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full border",
        styles.circle,
      )}
      aria-hidden
    >
      {icon === "card-off" ? (
        <>
          <CreditCard className={cn("h-5 w-5", styles.icon)} strokeWidth={2.3} />
          <Slash className={cn("absolute h-7 w-7", styles.icon)} strokeWidth={2.2} />
        </>
      ) : null}
      {icon === "bank" ? <Landmark className={cn("h-6 w-6", styles.icon)} strokeWidth={2.3} /> : null}
      {icon === "shield" ? <ShieldCheck className={cn("h-6 w-6", styles.icon)} strokeWidth={2.3} /> : null}
    </span>
  );
}

export function PricingTrustRow({ items }: PricingTrustRowProps) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-[0_20px_65px_-52px_rgba(15,23,42,0.42)] ring-1 ring-slate-100 sm:px-5">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-0">
        {items.map((item, index) => (
          <div
            key={item.title}
            className={cn(
              "flex min-w-0 flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3 lg:px-5",
              index > 0 ? "lg:border-l lg:border-slate-200" : undefined,
              index === 0 ? "lg:pl-0" : undefined,
              index === items.length - 1 ? "lg:pr-0" : undefined,
            )}
          >
            <TrustIcon icon={item.icon} iconLabel={item.iconLabel} tone={item.tone} />
            <div className="min-w-0">
              <h3 className="text-sm font-black leading-tight text-slate-950">{item.title}</h3>
              <p className="mt-1 text-xs font-medium leading-5 text-slate-600">{item.subtitle}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
